import { useState } from "react";
import { api } from "../../lib/api";
import { FileDown, Factory, History, RotateCcw, Eye, ShoppingCart } from "lucide-react";
import { Modal } from "../ui-bits";
import { Card, fmtDate } from "./common";
import { ProductionRunModal } from "./ProductionRunModal";

const FIELD_LABEL = { materials: "distinta base", sizes: "taglie", colors: "colori", size_measurements: "misure", techpack_draft: "lavorazioni",
  cost: "costo", retail_price: "prezzo", cost_confirmed: "conferma costo", measurements_confirmed: "conferma misure", sample_stage: "fase",
  dev_phase: "fase", responsible_id: "responsabile", sample_due_date: "scadenza", images: "immagini", main_image_id: "immagine", name: "nome", code: "codice",
  approved_at: "approvazione", approved_by: "approvazione", category: "categoria", collection_id: "collezione", trims: "accessori" };
const REASON = (r) => r === "edit" ? "Modifica" : r?.startsWith("stage:") ? "Cambio fase" : r?.startsWith("restore:") ? `Ripristino ${r.slice(8)}` : r === "production_sheet" ? "Scheda produzione" : r;

export function ProductionPanel({ detail, canEdit, onGenerate, busy, onChanged }) {
  const g = detail.garment;
  const [runSheet, setRunSheet] = useState(null);
  const approved = ["approved", "production"].includes(g.sample_stage);
  const download = async (s) => {
    const r = await api.get(`/sampling/production-sheets/${s.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `${s.number}.pdf`; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <Card title="Preparazione produzione" testId="sampling-production">
      {!approved ? <p className="text-sm opacity-70" data-testid="production-not-approved">La scheda produzione si genera dopo l'approvazione del campione. Nulla passa in produzione in automatico.</p>
        : !detail.ready ? (
          <div className="text-sm" data-testid="production-missing">
            <p className="text-orange-700 mb-2">Campione approvato, ma mancano ancora queste informazioni:</p>
            <ul className="list-disc pl-5 space-y-1">{detail.missing.map(c => <li key={c.key}>{c.label}</li>)}</ul>
          </div>
        ) : <p className="text-sm text-green-700" data-testid="production-ready">Tutto completo: puoi generare la scheda produzione con i dati definitivi.</p>}
      {canEdit && approved && (
        <button disabled={!detail.ready || busy} onClick={onGenerate} className="btn-gold inline-flex items-center gap-2 mt-4" data-testid="production-generate">
          <Factory size={14}/> {detail.sheets.length ? "Genera nuova versione scheda" : "Genera scheda produzione"}
        </button>
      )}
      {detail.sheets.length > 0 && (
        <ul className="mt-5 text-sm space-y-2" data-testid="production-sheets">
          {detail.sheets.map(s => (
            <li key={s.id} className="border-b pb-2 last:border-0" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center justify-between gap-3">
                <span><b>{s.number}</b> <span className="text-xs opacity-60">· {fmtDate(s.created_at)} · {s.created_by_name || ""}</span></span>
                <span className="flex gap-4">
                  {canEdit && <button onClick={() => setRunSheet(s)} className="text-xs inline-flex items-center gap-1 uppercase tracking-widest" data-testid={`sheet-order-${s.id}`}><ShoppingCart size={12}/> Ordina materiali</button>}
                  <button onClick={() => download(s)} className="text-xs inline-flex items-center gap-1 uppercase tracking-widest" data-testid={`sheet-pdf-${s.id}`}><FileDown size={12}/> PDF</button>
                </span>
              </div>
              {(s.runs || []).map(r => (
                <div key={r.id} className="text-xs opacity-70 mt-1" data-testid={`sheet-run-${r.id}`}>{fmtDate(r.at)} · {r.pieces} pezzi · ordini {r.po_numbers.join(", ")}
                  {r.production_id && <> · <a href={`/productions?open=${r.production_id}`} className="underline" data-testid={`sheet-run-prod-${r.id}`}>{r.production_number}</a></>}</div>
              ))}
            </li>
          ))}
        </ul>
      )}
      {runSheet && <ProductionRunModal sheet={runSheet} garment={g} onClose={() => setRunSheet(null)} onDone={onChanged}/>}
    </Card>
  );
}

export function VersionHistory({ detail, canEdit, onRestore, busy }) {
  const [view, setView] = useState(null);
  const open = async (v) => setView((await api.get(`/sampling/garments/${detail.garment.id}/versions/${v.id}`)).data);
  return (
    <Card title={<span className="inline-flex items-center gap-2"><History size={12}/> Versioni precedenti ({detail.versions.length})</span>} testId="sampling-versions">
      {detail.versions.length === 0 ? <div className="text-sm opacity-60">Ogni modifica salva qui la versione precedente.</div> : (
        <ul className="text-sm space-y-2 max-h-72 overflow-auto">
          {detail.versions.map(v => (
            <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 border-b pb-2 last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`version-${v.version}`}>
              <span><b>v{v.version}</b> <span className="text-xs opacity-60">· {fmtDate(v.at)} · {v.by_name || "—"} · {REASON(v.reason)}{v.changed?.length ? ` · ${[...new Set(v.changed.map(c => FIELD_LABEL[c] || c))].join(", ")}` : ""}</span></span>
              <span className="flex gap-3 text-xs">
                <button onClick={() => open(v)} className="inline-flex items-center gap-1 opacity-70 hover:opacity-100" data-testid={`version-view-${v.version}`}><Eye size={12}/> Vedi</button>
                {canEdit && detail.garment.sample_stage !== "production" && <button disabled={busy} onClick={() => window.confirm(`Ripristinare la versione ${v.version}? La versione attuale resta salvata nello storico.`) && onRestore(v)} className="inline-flex items-center gap-1 opacity-70 hover:opacity-100" data-testid={`version-restore-${v.version}`}><RotateCcw size={12}/> Ripristina</button>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {view && (
        <Modal open onClose={() => setView(null)} title={`Versione ${view.version} · ${fmtDate(view.at)}`} wide>
          <div className="text-sm space-y-2" data-testid="version-modal">
            <div><b>{view.snapshot.code}</b> — {view.snapshot.name}</div>
            <div>Taglie: {(view.snapshot.sizes || []).join(", ") || "—"} · Colori: {(view.snapshot.colors || []).join(", ") || "—"}</div>
            <div>Costo € {view.snapshot.cost || 0} · Prezzo € {view.snapshot.retail_price || 0}</div>
            <div>Distinta: {(view.snapshot.materials || []).length} righe · Misure: {(view.snapshot.size_measurements?.rows || []).length} righe</div>
            {view.snapshot.techpack_draft?.construction_notes && <div>Lavorazioni: {view.snapshot.techpack_draft.construction_notes}</div>}
          </div>
        </Modal>
      )}
    </Card>
  );
}
